import { Box, CardContent, Checkbox, Grid, Link as MuiLink, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, tooltipClasses, Typography } from '@mui/material';
import React, { useEffect, useState, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { dataConstants } from '../../../constants';
import { getRandomStr, roundNum } from '../../../helpers';
import getLatestPrice from '../../../../lib/stock/_getLatestPrice';
import { getPercent } from '../../../utils';
import Image from 'next/image';
import * as send from '../../../../lib/analytics/send';
import { convertToNYTime } from '../../../helpers/time';

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    boxShadow: theme.shadows[1],
    fontSize: 12,
    fontWeight: '600 !important',
  },
}));

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: '#fff',
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  boxShadow: theme.palette.mode === 'dark' ? 0 : '0 0.5px 1px rgb(255 255 255 / 20%), 0 2px 3px rgb(255 255 255 / 10%)',
  border: theme.palette.mode === 'dark' ? '1px solid rgb(19, 47, 76)' : '1px solid #eae9ee',
  borderRadius: '7px',
  '& a': { color: '#174c86' },
  '& .MuiTableContainer-root th': {
    background: '#fbfbfc',
    fontWeight: 600,
  },
  '& .MuiTableContainer-root::-webkit-scrollbar-thumb': {
    background: '#020e4a29',
    borderRadius: '1px'
  },
  '& .MuiTableContainer-root:hover::-webkit-scrollbar-thumb': {
    background: '#020e4a85',
    borderRadius: '1px'
  },
  '& .MuiTableContainer-root::-webkit-scrollbar-track': {
    background: '#f2f2f2',
    borderRadius: '1px'
  },
  '& .MuiTableContainer-root::-webkit-scrollbar': {
    width: '5px !important',
    height: '5px !important'
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: 'rgb(251 251 252)',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const getColorFromPercentage = (percentageStr) => {
  if (percentageStr) {
    const number = parseFloat(percentageStr?.replace('%', '').trim());
    return number < 0 ? '#AB1C39' : 'rgb(14, 158, 25)';
  }
  return "rgba(0, 0, 0, 0.87)";
};

const convertData = (data) => {
  return data.reduce((acc, { ticker, short_name, r, p }) => {
    if (!acc[ticker]) {
      acc[ticker] = { ticker, short_name, price_data: [] };
    }
    acc[ticker].price_data.push({ price: p });
    return acc;
  }, {});
};

/**
 * Reusable component to render standard stock metric tables
 * (All-Time Highs, Multi-Baggers, Recently Crushed)
 */
const StockCategoryTable = ({ title, description, tickers, stockTblPrice, loading, eventName }) => {
  if (!tickers || tickers.length === 0 || !stockTblPrice) return null;

  return (
    <Grid size={{ xs: 12, sm: 12, md: 4 }}>
      <Item>
        <Box component="div" className="col-md-3">
          <CardContent sx={{ background: '#fbfbfc', borderRadius: '6px 6px 0 0', pb: 1 }}>
            <Box component="div">
              <Typography sx={{ fontSize: 14, fontWeight: 600, mr: 1.3, mb: .3 }} color="text.color.white">
                {title}
              </Typography>
              <Typography variant="body2" component="div" sx={{ fontSize: '.75rem !important' }} color="text.secondary">
                {description}
              </Typography>
            </Box>
          </CardContent>
          <TableContainer sx={{ maxHeight: 437, overflow: "auto" }}>
            <Table stickyHeader aria-label={`${eventName}-table`} size="small">
              <TableHead sx={{ background: '#fbfbfc', '& th': { color: 'text.secondary', fontSize: '.8rem', pr: 0 }, '& th:last-child': { pr: 2 } }}>
                <TableRow>
                  <TableCell colSpan={2}>Company</TableCell>
                  <TableCell align="right" sx={{ width: '67px' }}>Price</TableCell>
                  <TableCell align="right" sx={{ width: '67px' }}>Chg %</TableCell>
                  <TableCell align="right" sx={{ width: '67px' }}>Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ '& .ticker-name': { whiteSpace: 'nowrap', position: 'absolute' } }}>
                {tickers.map((ticker) => {
                  const company = stockTblPrice[ticker];
                  if (!ticker && !company?.price_data?.length) return null;

                  const hasPriceData = company?.price_data?.length > 1;
                  const currentPrice = company?.price_data?.[0]?.price;
                  const prevPrice = company?.price_data?.[1]?.price;
                  const isPositive = currentPrice >= prevPrice;

                  return (
                    <StyledTableRow
                      hover key={getRandomStr(4)}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 }, '& td': { pr: 0 }, '& td:last-child': { pr: 2 } }}
                    >
                      <TableCell padding="checkbox" sx={{ p: '8px !important' }}>
                        <Checkbox
                          aria-describedby="enhanced-table"
                          sx={{ color: 'rgb(128,134,139)', '&:hover, &:hover.Mui-checked': { color: '#174ea6' }, '&.Mui-checked': { color: 'rgb(128,134,139)' } }}
                          size="small"
                          checked={false}
                          onClick={(e) => e.preventDefault()}
                          icon={
                            <Image
                              itemProp="logo"
                              alt={`${ticker || company?.ticker} logo`}
                              src={`https://logo.grufity.com/${ticker || company?.ticker}.png`}
                              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                              quality={50}
                              priority={false}
                              loading="lazy"
                              placeholder="empty"
                              width={25}
                              height={25}
                              style={{
                                height: 'auto',
                                background: '#f8f9fa',
                                border: '1px solid rgb(218,231,224)',
                                borderRadius: "5px",
                                objectFit: 'contain'
                              }}
                            />
                          }
                          inputProps={{ 'aria-labelledby': ticker }}
                        />
                      </TableCell>
                      <TableCell align="left" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', position: 'relative', pb: loading ? 1 : 3 }}>
                        <MuiLink
                          underline="hover"
                          sx={{ fontWeight: 600 }}
                          target="_blank"
                          href={`${dataConstants.NAVS.STOCK}/${ticker}`}
                          onClick={(e) => send.uevent(e)}
                          data-event-name={`${eventName}-table`}
                          data-event-category="stock-homepage"
                          data-event-label={`${dataConstants.NAVS.STOCK}/${ticker}`}
                        >
                          {ticker || company?.ticker}
                        </MuiLink>
                        {loading ? (
                          <Skeleton animation="wave" height={10} width="100%" style={{ marginTop: 3, marginLeft: 'auto', marginBottom: 0 }} />
                        ) : (
                          <Typography component="p" className="ticker-name" sx={{ fontSize: '.75rem' }}>
                            {company?.short_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {loading ? (
                          <Skeleton animation="wave" height={25} width="75%" style={{ marginTop: 0, marginLeft: 'auto' }} />
                        ) : currentPrice ? `$${roundNum(currentPrice)}` : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: isPositive ? 'rgb(14, 158, 25)' : '#AB1C39' }}>
                        {loading ? (
                          <Skeleton animation="wave" height={25} width="75%" style={{ marginTop: 0, marginLeft: 'auto' }} />
                        ) : hasPriceData && currentPrice && prevPrice ? `${getPercent(currentPrice, prevPrice)}%` : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: isPositive ? 'rgb(14, 158, 25)' : '#AB1C39' }}>
                        {loading ? (
                          <Skeleton animation="wave" height={25} width="75%" style={{ marginTop: 0, marginLeft: 'auto' }} />
                        ) : hasPriceData && currentPrice && prevPrice ? `${isPositive ? '+' : ''}${roundNum(currentPrice - prevPrice)}` : '-'}
                      </TableCell>
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Item>
    </Grid>
  );
};

const HighMultiCrushedEarnings = ({ highMultiCrushed, recentEarnings }) => {
  const [loading, setLoading] = useState(true);
  const [stockTblPrice, setStockTblPrice] = useState(null);

  const parsedHighMulti = useMemo(() => {
    try {
      return highMultiCrushed ? JSON.parse(highMultiCrushed) : {};
    } catch (e) {
      console.error('Failed to parse highMultiCrushed:', e);
      return {};
    }
  }, [highMultiCrushed]);

  const parsedEarnings = useMemo(() => {
    try {
      return recentEarnings ? JSON.parse(recentEarnings) : [];
    } catch (e) {
      console.error('Failed to parse recentEarnings:', e);
      return [];
    }
  }, [recentEarnings]);

  const { allTimeHigh = [], multiBaggers = [], recentlyCrushed = [] } = parsedHighMulti;

  const groupedEarnings = useMemo(() => {
    if (!Array.isArray(parsedEarnings) || parsedEarnings.length === 0) return [];
    
    return [...parsedEarnings]
      .sort((a, b) => new Date(b.Date) - new Date(a.Date))
      .reduce((acc, curr) => {
        const dateGroup = acc.find(group => group.date === curr.Date);
        if (dateGroup) {
          dateGroup.data.push(curr);
        } else {
          acc.push({ date: curr.Date, data: [curr] });
        }
        return acc;
      }, []);
  }, [parsedEarnings]);

  useEffect(() => {
    let isMounted = true;
    const combinedTickers = [...allTimeHigh, ...multiBaggers, ...recentlyCrushed];

    if (combinedTickers.length > 0) {
      setLoading(true);
      getLatestPrice(combinedTickers, false)
        .then(data => {
          if (isMounted) {
            setStockTblPrice(convertData(data?.getLatestPrice || []));
          }
        })
        .catch(error => console.error('Error fetching stock prices:', error))
        .finally(() => {
          if (isMounted) setLoading(false);
        });
    } else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [allTimeHigh, multiBaggers, recentlyCrushed]);

  return (
    <>
      <StockCategoryTable
        title="All-Time Highs"
        description="Stocks that have reached their highest prices ever."
        tickers={allTimeHigh}
        stockTblPrice={stockTblPrice}
        loading={loading}
        eventName="alltime"
      />

      <StockCategoryTable
        title="Multi-Baggers"
        description="Stocks that have multiplied their value several times over."
        tickers={multiBaggers}
        stockTblPrice={stockTblPrice}
        loading={loading}
        eventName="multibagger"
      />

      <StockCategoryTable
        title="Recently Crushed"
        description="Stocks that have crashed in the past few days."
        tickers={recentlyCrushed}
        stockTblPrice={stockTblPrice}
        loading={loading}
        eventName="recently-crushed"
      />

      {groupedEarnings.length > 0 && (
        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
          <Item>
            <Box component="div" className="col-md-3">
              <CardContent sx={{ background: '#fbfbfc', borderRadius: '6px 6px 0 0', pb: 1 }}>
                <Box component="div">
                  <Typography sx={{ fontSize: 14, fontWeight: 600, mr: 1.3, mb: .3 }} color="text.color.white">
                    Recent Earnings
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ fontSize: '.75rem !important' }} color="text.secondary">
                    Stocks that have reported earnings recently.
                  </Typography>
                </Box>
              </CardContent>
              <TableContainer sx={{ overflow: "auto" }}>
                <Table stickyHeader aria-label="earnings-table" size="small">
                  <TableHead sx={{ background: '#fbfbfc', '& th': { color: 'text.secondary', fontSize: '.8rem' } }}>
                    <TableRow>
                      <TableCell colSpan={2}>Ticker</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">YoY Rev</TableCell>
                      <TableCell align="right">Earnings</TableCell>
                      <TableCell align="right">YoY Earn</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ '& .ticker-name': { whiteSpace: 'nowrap', position: 'absolute' } }}>
                    {groupedEarnings.map((group, index) => {
                      let formattedDate = group.date;
                      try {
                        formattedDate = convertToNYTime(group.date);
                      } catch (error) {
                        console.error('Error formatting date:', error);
                      }
                      
                      return (
                        <React.Fragment key={index}>
                          {formattedDate && (
                            <StyledTableRow>
                              <TableCell colSpan={7} sx={{ color: "rgba(0, 0, 0, 0.6)", textAlign: "left", fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {formattedDate}
                              </TableCell>
                            </StyledTableRow>
                          )}
                          {group.data.map((d) => (
                            <StyledTableRow hover key={getRandomStr(4)} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '& td': { py: 1.2, whiteSpace: 'nowrap' } }}>
                              <TableCell padding="checkbox" sx={{ py: '8px !important' }}>
                                <Checkbox
                                  aria-describedby="enhanced-table"
                                  sx={{ color: 'rgb(128,134,139)', '&:hover, &:hover.Mui-checked': { color: '#174ea6' }, '&.Mui-checked': { color: 'rgb(128,134,139)' } }}
                                  size="small"
                                  checked={false}
                                  onClick={(e) => e.preventDefault()}
                                  icon={
                                    <Image
                                      itemProp="logo"
                                      alt={`${d.Ticker} logo`}
                                      src={`https://logo.grufity.com/${d.Ticker}.png`}
                                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                      quality={50}
                                      priority={false}
                                      loading="lazy"
                                      placeholder="empty"
                                      width={25}
                                      height={25}
                                      style={{
                                        height: 'auto',
                                        background: '#f8f9fa',
                                        border: '1px solid rgb(218,231,224)',
                                        borderRadius: "5px",
                                        objectFit: 'contain'
                                      }}
                                    />
                                  }
                                  inputProps={{ 'aria-labelledby': d.Ticker }}
                                />
                              </TableCell>
                              <TableCell>
                                <MuiLink
                                  underline="hover"
                                  sx={{ fontWeight: 600 }}
                                  target="_blank"
                                  href={`${dataConstants.NAVS.STOCK}/${d?.Ticker}`}
                                  onClick={(e) => send.uevent(e)}
                                  data-event-name="earnings-table"
                                  data-event-category="stock-homepage"
                                  data-event-label={`${dataConstants.NAVS.STOCK}/${d?.Ticker}`}
                                >
                                  {d?.Ticker || '-'}
                                </MuiLink>
                              </TableCell>
                              
                              <TableCell sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                {d?.Name && d?.Name.length > 10 ? (
                                  <LightTooltip title={d.Name} placement="left" disableInteractive>
                                    <span>{d.Name}</span>
                                  </LightTooltip>
                                ) : (
                                  d?.Name || '-'
                                )}
                              </TableCell>
                              
                              <TableCell align="right">{d?.Revenue || '-'}</TableCell>
                              <TableCell align="right" sx={{ color: getColorFromPercentage(d['YoY Rev']) }}>{d['YoY Rev'] || '-'}</TableCell>
                              <TableCell align="right">{d?.Earnings || '-'}</TableCell>
                              <TableCell align="right" sx={{ color: getColorFromPercentage(d['YoY Earn']) }}>{d['YoY Earn'] || '-'}</TableCell>
                            </StyledTableRow>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Item>
        </Grid>
      )}
    </>
  );
};

export default HighMultiCrushedEarnings;


// previously edited files for reference:

// import { Box, CardContent, Checkbox, Grid, Link, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, tooltipClasses, Typography } from '@mui/material';
// import React, { useEffect, useState } from 'react';
// // import Link from 'next/link';
// import { styled } from '@mui/material/styles';
// import { dataConstants } from '../../../constants';
// import { getRandomStr, roundNum } from '../../../helpers';
// // import getTickersInfo from '../../../../lib/stock/_getTickersInfo';
// import getLatestPrice from '../../../../lib/stock/_getLatestPrice';
// import { getPercent } from '../../../utils';
// // import groupBy from 'lodash/groupBy';
// // const groupBy = (x, f) => x && x.reduce((a, b) => ((a[f(b)] ||= []).push(b), a), {});
// // import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
// import Image from 'next/image';
// import * as send from '../../../../lib/analytics/send';
// import { convertToNYTime } from '../../../helpers/time';

// const LightTooltip = styled(({ className, ...props }) => (
//   <Tooltip {...props} classes={{ popper: className }} />
// ))(({ theme }) => ({
//   [`& .${tooltipClasses.tooltip}`]: {
//     backgroundColor: theme.palette.primary.main,
//     // color: 'rgba(0, 0, 0, 0.87)',
//     color: '#fff',
//     boxShadow: theme.shadows[1],
//     fontSize: 12,
//     fontWeight: '600 !important',
//   },
// }));

// const Item = styled(Paper)(({ theme }) => ({
//   backgroundColor: '#fff',
//   ...theme.typography.body2,
//   // padding: theme.spacing(1),
//   // margin: theme.spacing(.5),
//   // textAlign: 'center',
//   color: theme.palette.text.secondary,
//   boxShadow: theme.palette.mode === 'dark' ? 0 : '0 0.5px 1px rgb(255 255 255 / 20%), 0 2px 3px rgb(255 255 255 / 10%)',
//   border: theme.palette.mode === 'dark' ? '1px solid rgb(19, 47, 76)' : '1px solid #eae9ee',
//   borderRadius: '7px',
//   '& a': { color: '#174c86' },
//   '& .MuiTableContainer-root th': {
//     background: '#fbfbfc',
//     fontWeight: 600,
//   },
//   '& .MuiTableContainer-root::-webkit-scrollbar-thumb': {
//     background: '#020e4a29',
//     borderRadius: '1px'
//   },
//   '& .MuiTableContainer-root:hover::-webkit-scrollbar-thumb': {
//     background: '#020e4a85',
//     borderRadius: '1px'
//   },
//   '& .MuiTableContainer-root::-webkit-scrollbar-track': {
//     background: '#f2f2f2',
//     borderRadius: '1px'
//   },
//   '& .MuiTableContainer-root::-webkit-scrollbar': {
//     width: '5px !important',
//     height: '5px !important'
//   }
// }));

// const getColorFromPercentage = (percentageStr) => {
//   if (percentageStr) {
//     const number = parseFloat(percentageStr?.replace('%', '').trim());
//     return number < 0 ? '#AB1C39' : 'rgb(14, 158, 25)';
//   }
//   return "rgba(0, 0, 0, 0.87)"
// };

// const formatDate = (dateStr) => {
//   try {
//     const date = new Date(dateStr);
//     if (isNaN(date.getTime())) {
//       throw new Error('Invalid date');
//     }
//     const options = { day: '2-digit', month: 'short', year: 'numeric' };
//     const formattedDate = date.toLocaleDateString('en-US', options);
//     return formattedDate.replace(/(\d{2}) (\w{3})/, '$2 $1');
//   } catch (error) {
//     return '';
//   }
// };

// const StyledTableRow = styled(TableRow)(({ theme }) => ({
//   '&:nth-of-type(even)': {
//     backgroundColor: 'rgb(251 251 252)',
//   },
//   // hide last border
//   '&:last-child td, &:last-child th': {
//     border: 0,
//   },
// }));

// const convertData = (data) => {
//   const result = data.reduce((acc, { ticker, short_name, r, p }) => {
//     if (!acc[ticker]) {
//       acc[ticker] = {
//         ticker,
//         short_name,
//         price_data: [],
//       };
//     }

//     acc[ticker].price_data.push({ price: p });

//     return acc;
//   }, {});

//   return result;
// }

// const HighMultiCrushedEarnings = ({ highMultiCrushed, recentEarnings }) => {
//   const { allTimeHigh, multiBaggers, recentlyCrushed } = highMultiCrushed && highMultiCrushed !== "" ? JSON.parse(highMultiCrushed) : {}
//   const earnings = recentEarnings && recentEarnings !== "" ? JSON.parse(recentEarnings) : {}
//   const [loading, setLoading] = useState(true);
//   const [stockTblPrice, setStockTblPrice] = useState(null)
//   useEffect(() => {
//     if (highMultiCrushed && highMultiCrushed !== "") {
//       getLatestPrice([...allTimeHigh, ...multiBaggers, ...recentlyCrushed], false).then(data => setStockTblPrice(convertData(data?.getLatestPrice))).catch(error => console.error('multi-high', error)).finally(() => setLoading(false));
//     }
//   }, [highMultiCrushed]);

//   return ((allTimeHigh || multiBaggers) &&
//     <>
//       {allTimeHigh && allTimeHigh.length > 0 && stockTblPrice ?
//         <Grid size={{ xs: 12, sm: 12, md: 4 }}>
//           <Item>
//             <Box component="div" className="col-md-3">
//               <CardContent sx={{ background: '#fbfbfc', borderRadius: '6px 6px 0 0', pb: 1 }}>
//                 <Box component="div">
//                   <Typography sx={{ fontSize: 14, fontWeight: 600, mr: 1.3, mb: .3 }} color="text.color.white">
//                     {`All-Time Highs`}
//                   </Typography>
//                   <Typography variant="body2" component="div" sx={{ fontSize: '.75rem !important', }} color="text.secondary">{`Stocks that have reached their highest prices ever.`}</Typography>
//                 </Box>
//               </CardContent>
//               <TableContainer sx={{ maxHeight: 437, overflow: "auto" }}>
//                 <Table stickyHeader aria-label={`alltime-table`} size="small" >
//                   <TableHead sx={{ background: '#fbfbfc', '& th': { color: 'text.secondary', fontSize: '.8rem', pr: 0 }, '& th:last-child': { pr: 2 }, }}>
//                     <TableRow>
//                       {/* <TableCell /> */}
//                       <TableCell colSpan={2}>Company</TableCell>
//                       <TableCell align="right" sx={{ width: '67px' }}>Price</TableCell>
//                       <TableCell align="right" sx={{ width: '67px' }}>Chg %</TableCell>
//                       <TableCell align="right" sx={{ width: '67px' }}>Change</TableCell>
//                     </TableRow>
//                   </TableHead>
//                   <TableBody
//                     sx={{
//                       // '& .ticker-name': { whiteSpace: 'nowrap', textOverflow: 'ellipsis', width: '8rem', display: 'block', overflow: 'hidden', }
//                       '& .ticker-name': { whiteSpace: 'nowrap', position: 'absolute', }
//                     }}
//                   >
//                     {allTimeHigh?.map((d, i) => {
//                       const company = stockTblPrice ? stockTblPrice[d] : null
//                       return (
//                         ((d && loading) || (company && company?.price_data?.length > 0)) ?
//                           <StyledTableRow
//                             hover key={getRandomStr(4)}
//                             sx={{
//                               '&:last-child td, &:last-child th': { border: 0 },
//                               '& td': { pr: 0 }, '& td:last-child': { pr: 2 },
//                             }}
//                           >
//                             <TableCell padding="checkbox" sx={{ p: '8px !important' }}>
//                               <Checkbox
//                                 aria-describedby={"enhanced-table"}
//                                 sx={{ color: 'rgb(128,134,139)', '&:hover, &:hover.Mui-checked': { color: '#174ea6' }, '&.Mui-checked': { color: 'rgb(128,134,139)' } }}
//                                 size="small"
//                                 checked={false}
//                                 onClick={(e) => e.preventDefault()}
//                                 icon={<Image
//                                   itemProp="logo" alt={""}
//                                   src={`https://logo.grufity.com/${d ?? company?.ticker}.png`}
//                                   quality={50}
//                                   priority={false}
//                                   loading='lazy'
//                                   placeholder='empty'
//                                   width={25}
//                                   height={25}
//                                   style={{
//                                     height: 'auto',
//                                     background: '#f8f9faxx', border: '1px solid rgb(218,231,224)xx',
//                                     borderRadius: "5px", boxShadow: 0, color: '#174c86', '& img': { objectFit: 'contain', borderRadius: "4px" },
//                                   }}
//                                 />}
//                                 // icon={<Avatar itemProp="logo" alt={d} variant="rounded" src={`https://logo.grufity.com/${d ?? company?.ticker}.png`}
//                                 //   sx={{
//                                 //     width: 25, height: 25, background: '#f8f9faxx', border: '1px solid rgb(218,231,224)xx',
//                                 //     borderRadius: "5px", boxShadow: 0, color: '#174c86', '& img': { objectFit: 'contain', borderRadius: "4px" },
//                                 //   }}>
//                                 //   <ApartmentRoundedIcon sx={{ width: 25, height: 25, color: '#174ea6', }} />
//                                 // </Avatar>}
//                                 inputProps={{
//                                   'aria-labelledby': d,
//                                 }}
//                               />
//                             </TableCell>
//                             <TableCell align="left" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', position: 'relative', pb: loading ? 1 : 3 }}>
//                               <Link
//                                 underline="hover"
//                                 sx={{ fontWeight: 600 }}
//                                 key={getRandomStr(3)} target="_blank"
//                                 href={`${dataConstants.NAVS.STOCK}/${d}`}
//                                 onClick={(e) => send.uevent(e)}
//                                 data-event-name={'alltime-table'} data-event-category="stock-homepage" data-event-label={`${dataConstants.NAVS.STOCK}/${d}`}
//                               >
//                                 {d ?? company?.ticker}
//                               </Link>
//                               {loading ? (
//                                 <Skeleton animation="wave" height={10} width="100%" style={{ marginTop: 3, marginLeft: 'auto', marginBottom: 0 }} />
//                               ) : <Typography component="p" className="ticker-name" sx={{ fontSize: '.75rem' }}>{company?.short_name}</Typography>}
//                             </TableCell>
//                             <TableCell align="right">
//                               {loading ? (
//                                 <Skeleton animation="wave" height={25} width="75%" style={{ marginTop: 0, marginLeft: 'auto' }} />
//                               ) : company?.price_data?.length > 0 ? `$${roundNum(company?.price_data[0]?.price)}` : '-'}
//                             </TableCell>
//                             <TableCell align="right" sx={{ fontWeight: 600, color: `${company?.price_data[0]?.price >= company?.price_data[1]?.price ? 'rgb(14, 158, 25)' : '#AB1C39'}` }}>
//                               {loading ? (
//                                 <Skeleton animation="wave" height={25} width="75%" style={{ marginTop: 0, marginLeft: 'auto' }} />
//                               ) : company?.price_data?.length > 0 && company?.price_data[0]?.price && company?.price_data[1]?.price ? `${getPercent(company?.price_data[0]?.price, company?.price_data[1]?.price)}%` : '-'}
//                             </TableCell>
//                             <TableCell align="right" sx={{ fontWeight: 600, color: `${company?.price_data[0]?.price >= company?.price_data[1]?.price ? 'rgb(14, 158, 25)' : '#AB1C39'}`, }}>
//                               {loading ? (
//                                 <Skeleton animation="wave" height={25} width="75%" style={{ marginTop: 0, marginLeft: 'auto' }} />
//                               ) : company?.price_data[0]?.price - company?.price_data[1]?.price >= 0
//                                 ? `+${roundNum(company?.price_data[0]?.price - company?.price_data[1]?.price)}`
//                                 : company && company?.price_data[0]?.price && company?.price_data[1]?.price ? roundNum(company?.price_data[0]?.price - company?.price_data[1]?.price) : '-'}
//                             </TableCell>
//                           </StyledTableRow> : null)
//                     })}
//                   </TableBody>
//                 </Table>
//               </TableContainer>
//             </Box>
//           </Item>
//         </Grid> : null}
//       {multiBaggers && multiBaggers.length > 0 && stockTblPrice ?
//         <Grid size={{ xs: 12, sm: 12, md: 4 }}>
//           <Item>
//             <Box component="div" className="col-md-3">
//               <CardContent sx={{ background: '#fbfbfc', borderRadius: '6px 6px 0 0', pb: 1 }}>
//                 <Box component="div">
//                   <Typography sx={{ fontSize: 14, fontWeight: 600, mr: 1.3, mb: .3 }} color="text.color.white">
//                     {`Multi-Baggers`}
//                   </Typography>
//                   <Typography variant="body2" component="div" sx={{ fontSize: '.75rem !important', }} color="text.secondary">{`Stocks that have multiplied their value several times over.`}</Typography>
//                 </Box>
//               </CardContent>
//               <TableContainer sx={{ maxHeight: 437, overflow: "auto" }}>
//                 <Table stickyHeader aria-label={`multibagger-table`} size="small" >
//                   <TableHead sx={{ background: '#fbfbfc', '& th': { color: 'text.secondary', fontSize: '.8rem', pr: 0 }, '& th:last-child': { pr: 2 }, }}>
//                     <TableRow>
//                       {/* <TableCell /> */}
//                       <TableCell colSpan={2}>Company</TableCell>
//                       <TableCell align="right" sx={{ width: '67px' }}>Price</TableCell>
//                       <TableCell align="right" sx={{ width: '67px' }}>Chg %</TableCell>
//                       <TableCell align="right" sx={{ width: '67px' }}>Change</TableCell>
//                     </TableRow>
//                   </TableHead>
//                   <TableBody
//                     sx={{
//                       // '& .ticker-name': { whiteSpace: 'nowrap', textOverflow: 'ellipsis', width: '8rem', display: 'block', overflow: 'hidden', }
//                       '& .ticker-name': { whiteSpace: 'nowrap', position: 'absolute', }
//                     }}
//                   >
//                     {multiBaggers?.map((d, i) => {
//                       const company = stockTblPrice ? stockTblPrice[d] : null
//                       return (
//                         ((d && loading) || (company && company?.price_data?.length > 0)) ?
//                           <StyledTableRow
//                             hover key={getRandomStr(4)}
//                             sx={{
//                               '&:last-child td, &:last-child th': { border: 0 },
//                               '& td': { pr: 0 }, '& td:last-child': { pr: 2 },
//                             }}
//                           >
//                             <TableCell padding="checkbox" sx={{ p: '8px !important' }}>
//                               <Checkbox
//                                 aria-describedby={"enhanced-table"}
//                                 sx={{ color: 'rgb(128,134,139)', '&:hover, &:hover.Mui-checked': { color: '#174ea6' }, '&.Mui-checked': { color: 'rgb(128,134,139)' } }}
//                                 size="small"
//                                 checked={false}
//                                 onClick={(e) => e.preventDefault()}
//                                 icon={<Image
//                                   itemProp="logo" alt={""}
//                                   src={`https://logo.grufity.com/${d ?? company?.ticker}.png`}
//                                   sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
//                                   quality={50}
//                                   priority={false}
//                                   loading='lazy'
//                                   placeholder='empty'
//                                   width={25}
//                                   height={25}
//                                   style={{
//                                     height: 'auto',
//                                     background: '#f8f9faxx', border: '1px solid rgb(218,231,224)xx',
//                                     borderRadius: "5px", boxShadow: 0, color: '#174c86', '& img': { objectFit: 'contain', borderRadius: "4px" },
//                                   }}
//                                 />}
//                                 // icon={<Avatar itemProp="logo" alt={d} variant="rounded" src={`https://logo.grufity.com/${d ?? company?.ticker}.png`}
//                                 //   sx={{
//                                 //     width: 25, height: 25, background: '#f8f9faxx', border: '1px solid rgb(218,231,224)xx',
//                                 //     borderRadius: "5px", boxShadow: 0, color: '#174c86', '& img': { objectFit: 'contain', borderRadius: "4px" },
//                                 //   }}>
//                                 //   <ApartmentRoundedIcon sx={{ width: 25, height: 25, color: '#174ea6', }} />
//                                 // </Avatar>}
//                                 inputProps={{
//                                   'aria-labelledby': d,
//                                 }}
//                               />
//                             </TableCell>
//                             <TableCell align="left" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', position: 'relative', pb: loading ? 1 : 3 }}>
//                               <Link
//                                 underline="hover"
//                                 sx={{ fontWeight: 600 }}
//                                 key={getRandomStr(3)} target="_blank"
//                                 href={`${dataConstants.NAVS.STOCK}/${d}`}
//                                 onClick={(e) => send.uevent(e)}
//                                 data-event-name={'multibagger-table'} data-event-category="stock-homepage" data-event-label={`${dataConstants.NAVS.STOCK}/${d}`}
//                               >
//                                 {d ?? company?.ticker}
//                               </Link>
//                               {loading ? (
//                                 <Skeleton animation="wave" height={10} width="100%" style={{ marginTop: 3, marginLeft: 'auto', marginBottom: 0 }} />
//                               ) : <Typography component="p" className="ticker-name" sx={{ fontSize: '.75rem' }}>{company?.short_name}</Typography>}
//                             </TableCell>
//                             <TableCell align="right">
//                               {loading ? (
//                                 <Skeleton animation="wave" height={25} width="75%" style={{ marginTop: 0, marginLeft: 'auto' }} />
//                               ) : company?.price_data?.length > 0 ? `$${roundNum(company?.price_data[0]?.price)}` : '-'}
//                             </TableCell>
//                             <TableCell align="right" sx={{ fontWeight: 600, color: `${company?.price_data[0]?.price >= company?.price_data[1]?.price ? 'rgb(14, 158, 25)' : '#AB1C39'}` }}>
//                               {loading ? (
//                                 <Skeleton animation="wave" height={25} width="75%" style={{ marginTop: 0, marginLeft: 'auto' }} />
//                               ) : company?.price_data?.length > 0 && company?.price_data[0]?.price && company?.price_data[1]?.price ? `${getPercent(company?.price_data[0]?.price, company?.price_data[1]?.price)}%` : '-'}
//                             </TableCell>
//                             <TableCell align="right" sx={{ fontWeight: 600, color: `${company?.price_data[0]?.price >= company?.price_data[1]?.price ? 'rgb(14, 158, 25)' : '#AB1C39'}`, }}>
//                               {loading ? (
//                                 <Skeleton animation="wave" height={25} width="75%" style={{ marginTop: 0, marginLeft: 'auto' }} />
//                               ) : company?.price_data[0]?.price - company?.price_data[1]?.price >= 0
//                                 ? `+${roundNum(company?.price_data[0]?.price - company?.price_data[1]?.price)}`
//                                 : company && company?.price_data[0]?.price && company?.price_data[1]?.price ? roundNum(company?.price_data[0]?.price - company?.price_data[1]?.price) : '-'}
//                             </TableCell>
//                           </StyledTableRow> : null)
//                     })}
//                   </TableBody>
//                 </Table>
//               </TableContainer>
//             </Box>
//           </Item>
//         </Grid> : null}
//       {recentlyCrushed && recentlyCrushed.length > 0 && stockTblPrice ?
//         <Grid size={{ xs: 12, sm: 12, md: 4 }}>
//           <Item>
//             <Box component="div" className="col-md-3">
//               <CardContent sx={{ background: '#fbfbfc', borderRadius: '6px 6px 0 0', pb: 1 }}>
//                 <Box component="div">
//                   <Typography sx={{ fontSize: 14, fontWeight: 600, mr: 1.3, mb: .3 }} color="text.color.white">
//                     {`Recently Crushed`}
//                   </Typography>
//                   <Typography variant="body2" component="div" sx={{ fontSize: '.75rem !important', }} color="text.secondary">{`Stocks that have crashed in the past few days.`}</Typography>
//                 </Box>
//               </CardContent>
//               <TableContainer sx={{ maxHeight: 437, overflow: "auto" }}>
//                 <Table stickyHeader aria-label={`recently-crushed-table`} size="small" >
//                   <TableHead sx={{ background: '#fbfbfc', '& th': { color: 'text.secondary', fontSize: '.8rem', pr: 0 }, '& th:last-child': { pr: 2 }, }}>
//                     <TableRow>
//                       {/* <TableCell /> */}
//                       <TableCell colSpan={2}>Company</TableCell>
//                       <TableCell align="right" sx={{ width: '67px' }}>Price</TableCell>
//                       <TableCell align="right" sx={{ width: '67px' }}>Chg %</TableCell>
//                       <TableCell align="right" sx={{ width: '67px' }}>Change</TableCell>
//                     </TableRow>
//                   </TableHead>
//                   <TableBody
//                     sx={{
//                       // '& .ticker-name': { whiteSpace: 'nowrap', textOverflow: 'ellipsis', width: '8rem', display: 'block', overflow: 'hidden', }
//                       '& .ticker-name': { whiteSpace: 'nowrap', position: 'absolute', }
//                     }}
//                   >
//                     {recentlyCrushed?.map((d, i) => {
//                       const company = stockTblPrice ? stockTblPrice[d] : null
//                       return (
//                         ((d && loading) || (company && company?.price_data?.length > 0)) ?
//                           <StyledTableRow
//                             hover key={getRandomStr(4)}
//                             sx={{
//                               '&:last-child td, &:last-child th': { border: 0 },
//                               '& td': { pr: 0 }, '& td:last-child': { pr: 2 },
//                             }}
//                           >
//                             <TableCell padding="checkbox" sx={{ p: '8px !important' }}>
//                               <Checkbox
//                                 aria-describedby={"enhanced-table"}
//                                 sx={{ color: 'rgb(128,134,139)', '&:hover, &:hover.Mui-checked': { color: '#174ea6' }, '&.Mui-checked': { color: 'rgb(128,134,139)' } }}
//                                 size="small"
//                                 checked={false}
//                                 onClick={(e) => e.preventDefault()}
//                                 icon={<Image
//                                   itemProp="logo" alt={""}
//                                   src={`https://logo.grufity.com/${d ?? company?.ticker}.png`}
//                                   sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
//                                   quality={50}
//                                   priority={false}
//                                   loading='lazy'
//                                   placeholder='empty'
//                                   width={25}
//                                   height={25}
//                                   style={{
//                                     height: 'auto',
//                                     background: '#f8f9faxx', border: '1px solid rgb(218,231,224)xx',
//                                     borderRadius: "5px", boxShadow: 0, color: '#174c86', '& img': { objectFit: 'contain', borderRadius: "4px" },
//                                   }}
//                                 />}
//                                 // icon={<Avatar itemProp="logo" alt={d} variant="rounded" src={`https://logo.grufity.com/${d ?? company?.ticker}.png`}
//                                 //   sx={{
//                                 //     width: 25, height: 25, background: '#f8f9faxx', border: '1px solid rgb(218,231,224)xx',
//                                 //     borderRadius: "5px", boxShadow: 0, color: '#174c86', '& img': { objectFit: 'contain', borderRadius: "4px" },
//                                 //   }}>
//                                 //   <ApartmentRoundedIcon sx={{ width: 25, height: 25, color: '#174ea6', }} />
//                                 // </Avatar>}
//                                 inputProps={{
//                                   'aria-labelledby': d,
//                                 }}
//                               />
//                             </TableCell>
//                             <TableCell align="left" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', position: 'relative', pb: loading ? 1 : 3 }}>
//                               <Link
//                                 underline="hover"
//                                 sx={{ fontWeight: 600 }}
//                                 key={getRandomStr(3)} target="_blank"
//                                 href={`${dataConstants.NAVS.STOCK}/${d}`}
//                                 onClick={(e) => send.uevent(e)}
//                                 data-event-name={'recently-crushed-table'} data-event-category="stock-homepage" data-event-label={`${dataConstants.NAVS.STOCK}/${d}`}
//                               >
//                                 {d ?? company?.ticker}
//                               </Link>
//                               {loading ? (
//                                 <Skeleton animation="wave" height={10} width="100%" style={{ marginTop: 3, marginLeft: 'auto', marginBottom: 0 }} />
//                               ) : <Typography component="p" className="ticker-name" sx={{ fontSize: '.75rem' }}>{company?.short_name}</Typography>}
//                             </TableCell>
//                             <TableCell align="right">
//                               {loading ? (
//                                 <Skeleton animation="wave" height={25} width="75%" style={{ marginTop: 0, marginLeft: 'auto' }} />
//                               ) : company?.price_data?.length > 0 ? `$${roundNum(company?.price_data[0]?.price)}` : '-'}
//                             </TableCell>
//                             <TableCell align="right" sx={{ fontWeight: 600, color: `${company?.price_data[0]?.price >= company?.price_data[1]?.price ? 'rgb(14, 158, 25)' : '#AB1C39'}` }}>
//                               {loading ? (
//                                 <Skeleton animation="wave" height={25} width="75%" style={{ marginTop: 0, marginLeft: 'auto' }} />
//                               ) : company?.price_data?.length > 0 && company?.price_data[0]?.price && company?.price_data[1]?.price ? `${getPercent(company?.price_data[0]?.price, company?.price_data[1]?.price)}%` : '-'}
//                             </TableCell>
//                             <TableCell align="right" sx={{ fontWeight: 600, color: `${company?.price_data[0]?.price >= company?.price_data[1]?.price ? 'rgb(14, 158, 25)' : '#AB1C39'}`, }}>
//                               {loading ? (
//                                 <Skeleton animation="wave" height={25} width="75%" style={{ marginTop: 0, marginLeft: 'auto' }} />
//                               ) : company?.price_data[0]?.price - company?.price_data[1]?.price >= 0
//                                 ? `+${roundNum(company?.price_data[0]?.price - company?.price_data[1]?.price)}`
//                                 : company && company?.price_data[0]?.price && company?.price_data[1]?.price ? roundNum(company?.price_data[0]?.price - company?.price_data[1]?.price) : '-'}
//                             </TableCell>
//                           </StyledTableRow> : null)
//                     })}
//                   </TableBody>
//                 </Table>
//               </TableContainer>
//             </Box>
//           </Item>
//         </Grid> : null}
//       {earnings && earnings.length > 0 ?
//         <Grid size={{ xs: 12, sm: 12, md: 6 }}>
//           <Item>
//             <Box component="div" className="col-md-3">
//               <CardContent sx={{ background: '#fbfbfc', borderRadius: '6px 6px 0 0', pb: 1 }}>
//                 <Box component="div">
//                   <Typography sx={{ fontSize: 14, fontWeight: 600, mr: 1.3, mb: .3 }} color="text.color.white">
//                     {`Recent Earnings`}
//                   </Typography>
//                   <Typography variant="body2" component="div" sx={{ fontSize: '.75rem !important', }} color="text.secondary">{`Stocks that have reported earnings recently.`}</Typography>
//                 </Box>
//               </CardContent>
//               <TableContainer sx={{ overflow: "auto" }}>
//                 <Table stickyHeader aria-label={`earnings-table`} size="small">
//                   <TableHead sx={{ background: '#fbfbfc', '& th': { color: 'text.secondary', fontSize: '.8rem' } }}>
//                     <TableRow>
//                       {/* <TableCell>Date</TableCell> */}
//                       <TableCell colSpan={2}>Ticker</TableCell>
//                       <TableCell>Name</TableCell>
//                       <TableCell align="right">Revenue</TableCell>
//                       <TableCell align="right">YoY Rev</TableCell>
//                       <TableCell align="right">Earnings</TableCell>
//                       <TableCell align="right">YoY Earn</TableCell>
//                     </TableRow>
//                   </TableHead>
//                   <TableBody sx={{ '& .ticker-name': { whiteSpace: 'nowrap', position: 'absolute', } }}>
//                     {earnings
//                       ?.sort((a, b) => new Date(b.Date) - new Date(a.Date))
//                       ?.reduce((acc, curr) => {
//                         const dateGroup = acc.find(group => group.date === curr.Date);
//                         if (dateGroup) {
//                           dateGroup.data.push(curr);
//                         } else {
//                           acc.push({ date: curr.Date, data: [curr] });
//                         }
//                         return acc;
//                       }, [])
//                       ?.map((group, index) => {
//                         let formattedDate;
//                         try {
//                           formattedDate = convertToNYTime(group.date);
//                         } catch (error) {
//                           formattedDate = group.date;
//                         }
//                         return (
//                           <React.Fragment key={index}>
//                             {formattedDate ?
//                               <StyledTableRow>
//                                 <TableCell colSpan={7} sx={{ color: "rgba(0, 0, 0, 0.6)", textAlign: "left", fontWeight: 600, whiteSpace: 'nowrap' }}>
//                                   {formattedDate}
//                                 </TableCell>
//                               </StyledTableRow> : null}
//                             {group.data.map((d, i) => (
//                               <StyledTableRow hover key={getRandomStr(4)} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '& td': { py: 1.2, whiteSpace: 'nowrap' } }}>
//                                 <TableCell padding="checkbox" sx={{ py: '8px !important' }}>
//                                   <Checkbox
//                                     aria-describedby={"enhanced-table"}
//                                     sx={{ color: 'rgb(128,134,139)', '&:hover, &:hover.Mui-checked': { color: '#174ea6' }, '&.Mui-checked': { color: 'rgb(128,134,139)' } }}
//                                     size="small"
//                                     checked={false}
//                                     onClick={(e) => e.preventDefault()}
//                                     icon={<Image
//                                       itemProp="logo" alt={""}
//                                       src={`https://logo.grufity.com/${d.Ticker}.png`}
//                                       sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
//                                       quality={50}
//                                       priority={false}
//                                       loading='lazy'
//                                       placeholder='empty'
//                                       width={25}
//                                       height={25}
//                                       style={{
//                                         height: 'auto',
//                                         background: '#f8f9faxx', border: '1px solid rgb(218,231,224)xx',
//                                         borderRadius: "5px", boxShadow: 0, color: '#174c86', '& img': { objectFit: 'contain', borderRadius: "4px" },
//                                       }}
//                                     />}
//                                     // icon={<Avatar itemProp="logo" alt={d} variant="rounded" src={`https://logo.grufity.com/${d?.Ticker}.png`}
//                                     //   sx={{
//                                     //     width: 25, height: 25, background: '#f8f9faxx', border: '1px solid rgb(218,231,224)xx',
//                                     //     borderRadius: "5px", boxShadow: 0, color: '#174c86', '& img': { objectFit: 'contain', borderRadius: "4px" },
//                                     //   }}>
//                                     //   <ApartmentRoundedIcon sx={{ width: 25, height: 25, color: '#174ea6', }} />
//                                     // </Avatar>}
//                                     inputProps={{ 'aria-labelledby': d }}
//                                   />
//                                 </TableCell>
//                                 <TableCell>
//                                   <Link
//                                     underline="hover"
//                                     sx={{ fontWeight: 600 }}
//                                     target="_blank"
//                                     href={`${dataConstants.NAVS.STOCK}/${d?.Ticker}`}
//                                     onClick={(e) => send.uevent(e)}
//                                     data-event-name={'earnings-table'} data-event-category="stock-homepage" data-event-label={`${dataConstants.NAVS.STOCK}/${d?.Ticker}`}
//                                   >
//                                     {d?.Ticker ? d?.Ticker : '-'}
//                                   </Link>
//                                 </TableCell>
//                                 {d?.Name && d?.Name.length > 10 ?
//                                   <LightTooltip title={d?.Name ? d?.Name : ""} placement="left" disableInteractive>
//                                     <TableCell sx={{ color: "rgba(0, 0, 0, 0.6)" }}>{d?.Name ? d?.Name : '-'}</TableCell>
//                                   </LightTooltip> :
//                                   <TableCell sx={{ color: "rgba(0, 0, 0, 0.6)" }}>{d?.Name ? d?.Name : '-'}</TableCell>}
//                                 <TableCell align="right">{d?.Revenue ? d?.Revenue : '-'}</TableCell>
//                                 <TableCell align="right" sx={{ color: getColorFromPercentage(d['YoY Rev']) }}>{d['YoY Rev'] ? d['YoY Rev'] : '-'}</TableCell>
//                                 <TableCell align="right">{d?.Earnings ? d?.Earnings : '-'}</TableCell>
//                                 <TableCell align="right" sx={{ color: getColorFromPercentage(d['YoY Earn']) }}>{d['YoY Earn'] ? d['YoY Earn'] : '-'}</TableCell>
//                               </StyledTableRow>))}
//                           </React.Fragment>)
//                       })}
//                   </TableBody>
//                 </Table>
//               </TableContainer>
//             </Box>
//           </Item>
//         </Grid> : null}
//     </>
//   )
// }

// export default (HighMultiCrushedEarnings)
